// Phase 3.5 — Pricing Graph: snapshot-bound actual series + GP% mode + Unresolved (N) indicator.
// CONTRACT:
//  - Suggested curve: derived from CURRENT shop matrix (it's a current recommendation).
//  - Actual series: ONLY plots parts_lines with ALL snapshot fields present
//    (cost_each_snapshot, quantity_snapshot, matrix_multiplier_snapshot,
//     matrix_quantity_mode_snapshot, and a resolved authoritative actual).
//  - Lines missing any snapshot field are EXCLUDED from the actual curve and
//    surfaced separately as "Unresolved / legacy lines (N)" — never fabricated
//    from current config.
import { useMemo, useState } from 'react';
// DTC-K: route the pricing-graph window anchor through the canonical
// clock so analytics queries are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import {
  ComposedChart, Line, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useShopPricingConfig } from '@/hooks/pricing/useShopPricingConfig';
import { usePartsLinesForGraph } from '@/hooks/pricing/usePartsLinesForGraph';
import { suggestedSellHybrid } from '@/lib/parts-financial/calculator';

interface Props { shopId: string; }

type DisplayMode = 'both' | 'suggested' | 'actual';
type YAxisMode = 'sell' | 'markup' | 'gp' | 'multiplier';
type DateRange = '7d' | '30d' | '90d';

const SOURCE_COLORS: Record<string, string> = {
  manual: 'hsl(var(--accent-foreground))',
  matrix: 'hsl(var(--primary))',
  flat_rate: 'hsl(var(--secondary-foreground))',
  optiguard: 'hsl(var(--destructive))',
  imported: 'hsl(var(--muted-foreground))',
  other: 'hsl(var(--muted-foreground))',
};

const sampleCosts = (max: number): number[] => {
  const points: number[] = [];
  const minLog = 0;
  const maxLog = Math.log10(Math.max(10, max));
  for (let i = 0; i <= 50; i++) {
    const v = Math.pow(10, minLog + (maxLog - minLog) * (i / 50));
    points.push(Number(v.toFixed(2)));
  }
  return points;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

function projectY(cost: number, sell: number, mode: YAxisMode): number {
  if (cost <= 0 || sell <= 0) return 0;
  switch (mode) {
    case 'sell':       return round2(sell);
    case 'markup':     return round2(((sell - cost) / cost) * 100);
    case 'gp':         return round2(((sell - cost) / sell) * 100);
    case 'multiplier': return round2(sell / cost);
  }
}

export function PricingGraph({ shopId }: Props) {
  const { config } = useShopPricingConfig(shopId);

  const [display, setDisplay] = useState<DisplayMode>('both');
  const [yMode, setYMode] = useState<YAxisMode>('sell');
  const [range, setRange] = useState<DateRange>('30d');
  const [category, setCategory] = useState<string>('__all__');
  const [overridesOnly, setOverridesOnly] = useState(false);
  const [excludeCanned, setExcludeCanned] = useState(true);

  const fromDate = useMemo(() => {
    const d = clock.now();
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    d.setDate(d.getDate() - days);
    return d;
  }, [range]);

  const linesQ = usePartsLinesForGraph({
    shopId,
    from: fromDate,
    category: category === '__all__' ? null : category,
    excludeCanned,
    overridesOnly,
    limit: 500,
  });

  const resolved = linesQ.data?.resolved ?? [];
  const unresolvedCount = linesQ.data?.unresolvedCount ?? 0;

  const allCategories = useMemo(() => {
    const set = new Set<string>();
    for (const k of config.categoryRules.keys()) set.add(k);
    for (const k of config.matrixByCategory.keys()) if (k !== '__default__') set.add(k);
    for (const r of resolved) if (r.category) set.add(r.category);
    return Array.from(set).sort();
  }, [config, resolved]);

  const maxCost = Math.max(2000, ...resolved.map((r) => r.cost_each));

  const suggestedCurve = useMemo(() => {
    const cat = category === '__all__' ? null : category;
    return sampleCosts(maxCost).map((cost) => {
      const r = suggestedSellHybrid(cost, cat, config);
      return { cost, suggested: projectY(cost, r.suggested, yMode) };
    });
  }, [config, category, maxCost, yMode]);

  const scatterData = useMemo(() => {
    return resolved.map((r) => {
      const cost = r.cost_each;
      const sell = r.actual_sell_each; // mode-aware: each is display authority on per-unit; derived on grouped
      return {
        x: cost,
        y: projectY(cost, sell, yMode),
        source: r.pricing_source,
        method: r.pricing_method,
        category: r.category ?? '—',
        sell,
        sellTotal: r.actual_sell_total,
        gpPct: cost > 0 && sell > 0 ? round2(((sell - cost) / sell) * 100) : 0,
        isCanned: r.is_canned,
        locked: r.manual_price_locked,
        mode: r.matrix_quantity_mode_snapshot,
      };
    });
  }, [resolved, yMode]);

  const yLabel =
    yMode === 'sell' ? 'Sell ($)' :
    yMode === 'markup' ? 'Markup %' :
    yMode === 'gp' ? 'GP %' :
    'Multiplier';

  const sourcesInData = Array.from(new Set(scatterData.map((d) => d.source)));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing Intelligence Graph</CardTitle>
        <CardDescription>
          Suggested curve (current matrix) vs actual sold parts (snapshot-bound).
          Suggestion-only — labels are estimates, not invoice prices.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Display</Label>
            <Select value={display} onValueChange={(v) => setDisplay(v as DisplayMode)}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both</SelectItem>
                <SelectItem value="suggested">Suggested only</SelectItem>
                <SelectItem value="actual">Actual only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Y-axis</Label>
            <Select value={yMode} onValueChange={(v) => setYMode(v as YAxisMode)}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sell">Sell price</SelectItem>
                <SelectItem value="markup">Markup %</SelectItem>
                <SelectItem value="gp">GP %</SelectItem>
                <SelectItem value="multiplier">Multiplier</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Range</Label>
            <Select value={range} onValueChange={(v) => setRange(v as DateRange)}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 days</SelectItem>
                <SelectItem value="30d">30 days</SelectItem>
                <SelectItem value="90d">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {allCategories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={overridesOnly} onCheckedChange={setOverridesOnly} id="overrides-only" />
            <Label htmlFor="overrides-only" className="text-xs">Overrides only</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={excludeCanned} onCheckedChange={setExcludeCanned} id="exclude-canned" />
            <Label htmlFor="exclude-canned" className="text-xs">Exclude canned jobs</Label>
          </div>
        </div>

        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                type="number" dataKey="x" name="Cost"
                tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                label={{ value: 'Part cost ($)', position: 'insideBottom', offset: -4, style: { fontSize: 11 } }}
              />
              <YAxis
                type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                label={{ value: yLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
              />
              <ZAxis range={[40, 40]} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p: any = payload[0].payload;
                  if (p.suggested !== undefined && p.cost !== undefined) {
                    return (
                      <div className="rounded-md bg-popover border p-2 text-xs shadow">
                        <div>Cost: ${p.cost}</div>
                        <div>Suggested {yLabel}: {p.suggested}</div>
                      </div>
                    );
                  }
                  return (
                    <div className="rounded-md bg-popover border p-2 text-xs shadow space-y-0.5">
                      <div>Category: <span className="font-medium">{p.category}</span></div>
                      <div>Cost: ${p.x}</div>
                      <div>Sell each: ${p.sell}</div>
                      {p.mode === 'grouped' && <div>Sell total: ${p.sellTotal}</div>}
                      <div>GP%: {p.gpPct}%</div>
                      <div>Source: <span className="font-medium">{p.source}</span> · {p.method}</div>
                      {p.locked && <div className="text-destructive">Manual override</div>}
                      {p.isCanned && <div className="text-destructive">Canned job</div>}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {(display === 'both' || display === 'suggested') && (
                <Line
                  data={suggestedCurve}
                  dataKey="suggested"
                  type="monotone"
                  name="Suggested curve"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
              {(display === 'both' || display === 'actual') && sourcesInData.map((src) => (
                <Scatter
                  key={src}
                  name={`Actual (${src})`}
                  data={scatterData.filter((d) => d.source === src)}
                  fill={SOURCE_COLORS[src] ?? 'hsl(var(--muted-foreground))'}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>
            Showing {scatterData.length} snapshot-bound actual point{scatterData.length === 1 ? '' : 's'}
            {' · '}
            {suggestedCurve.length} suggested-curve samples.
          </span>
          {unresolvedCount > 0 && (
            <TooltipProvider>
              <UiTooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="gap-1.5 cursor-help border-destructive/40 text-destructive">
                    <AlertTriangle className="h-3 w-3" />
                    Unresolved / legacy lines ({unresolvedCount})
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  These parts lines are missing one or more snapshot fields
                  (cost, quantity, matrix tier, multiplier, or quantity mode at the time of sale)
                  and cannot be safely placed on the historical curve.
                  They are excluded from the Actual series — never fabricated from current settings.
                </TooltipContent>
              </UiTooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          Suggested-only labels — never used as invoice or final pricing.
        </div>
      </CardContent>
    </Card>
  );
}
